import axiosClient from "./apiClient";
import { pollMqttEvents, extractReqId } from "./mqttEvents";

// One unlock/lock window in the UI model. Times are local wall-clock.
// start = stove unlocks (usable); end = stove locks + powers off.
export type ScheduleEntry = {
  startHour: number;
  startMinute: number;
  endHour: number;
  endMinute: number;
};

// Keyed by UI day: 0=Mon … 6=Sun. Each day holds up to MAX_WINDOWS_PER_DAY windows.
export type WeekSchedule = Record<number, ScheduleEntry[]>;

export const MAX_WINDOWS_PER_DAY = 4;

// Firmware wire entry. day: 0=Sun … 6=Sat (UTC). locked: 0=unlock, 1=lock.
type ScheduleEntryDto = {
  day: number;
  locked: number;
  hour: number;
  minute: number;
};

type StoveSettings = {
  schedulerActive: number;
  entries: ScheduleEntryDto[];
};

export function emptyWeekSchedule(): WeekSchedule {
  const out: WeekSchedule = {};
  for (let i = 0; i < 7; i++) out[i] = [];
  return out;
}

// (backendDay, hour, minute) local wall-clock → UTC. backendDay/result day are
// 0=Sun … 6=Sat (matches JS Date.getDay/getUTCDay). Anchored to the current
// week so the result reflects the current DST offset.
function localToUtc(
  backendDay: number,
  hour: number,
  minute: number
): { day: number; hour: number; minute: number } {
  const now = new Date();
  const daysToAdd = (backendDay - now.getDay() + 7) % 7;
  const local = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + daysToAdd,
    hour,
    minute
  );
  return {
    day: local.getUTCDay(),
    hour: local.getUTCHours(),
    minute: local.getUTCMinutes(),
  };
}

// Inverse of localToUtc: (backendDay, hour, minute) UTC → local wall-clock.
function utcToLocal(
  backendDay: number,
  hour: number,
  minute: number
): { day: number; hour: number; minute: number } {
  const nowUtc = new Date();
  const daysToAdd = (backendDay - nowUtc.getUTCDay() + 7) % 7;
  const utc = new Date(
    Date.UTC(
      nowUtc.getUTCFullYear(),
      nowUtc.getUTCMonth(),
      nowUtc.getUTCDate() + daysToAdd,
      hour,
      minute
    )
  );
  return {
    day: utc.getDay(),
    hour: utc.getHours(),
    minute: utc.getMinutes(),
  };
}

// Convert the UI model → firmware wire payload.
// UI day 0=Mon … 6=Sun → backend day 0=Sun … 6=Sat: backendDay = (uiDay + 1) % 7.
export function scheduleToDto(
  schedule: WeekSchedule,
  schedulerActive: boolean
): { is_scheduler_enabled: number; schedule: ScheduleEntryDto[] } {
  const entries: ScheduleEntryDto[] = [];
  for (let uiDay = 0; uiDay < 7; uiDay++) {
    const backendDay = (uiDay + 1) % 7;
    let windows = schedule[uiDay] ?? [];
    if (windows.length > MAX_WINDOWS_PER_DAY) {
      windows = windows.slice(0, MAX_WINDOWS_PER_DAY);
    }
    for (const w of windows) {
      const unlock = localToUtc(backendDay, w.startHour, w.startMinute);
      entries.push({
        day: unlock.day,
        locked: 0,
        hour: unlock.hour,
        minute: unlock.minute,
      });
      const lock = localToUtc(backendDay, w.endHour, w.endMinute);
      entries.push({
        day: lock.day,
        locked: 1,
        hour: lock.hour,
        minute: lock.minute,
      });
    }
  }
  return {
    is_scheduler_enabled: schedulerActive ? 1 : 0,
    schedule: entries,
  };
}

// Convert firmware wire entries → UI model. UTC→local first, then group by day
// and pair sorted unlock/lock entries (earliest unlock with earliest lock).
export function dtoToSchedule(dtoEntries: ScheduleEntryDto[]): WeekSchedule {
  const local = dtoEntries.map((e) => {
    const l = utcToLocal(e.day, e.hour, e.minute);
    return { day: l.day, locked: e.locked, hour: l.hour, minute: l.minute };
  });

  const byDay = new Map<number, typeof local>();
  for (const e of local) {
    const arr = byDay.get(e.day) ?? [];
    arr.push(e);
    byDay.set(e.day, arr);
  }

  const result = emptyWeekSchedule();
  byDay.forEach((dayEntries, backendDay) => {
    const uiDay = (backendDay + 6) % 7;
    const byTime = (a: { hour: number; minute: number }, b: { hour: number; minute: number }) =>
      a.hour * 60 + a.minute - (b.hour * 60 + b.minute);
    const unlocks = dayEntries.filter((e) => e.locked === 0).sort(byTime);
    const locks = dayEntries.filter((e) => e.locked === 1).sort(byTime);
    const count = Math.min(unlocks.length, locks.length);
    for (let i = 0; i < count; i++) {
      result[uiDay].push({
        startHour: unlocks[i].hour,
        startMinute: unlocks[i].minute,
        endHour: locks[i].hour,
        endMinute: locks[i].minute,
      });
    }
  });
  return result;
}

/**
 * Whether the stove is inside a scheduled *unlock* window right now, i.e. the
 * schedule says it should be usable. Outside every window the schedule keeps the
 * stove locked/off, so `false` here means "the schedule wants it locked now."
 * Handles windows that wrap past midnight (a previous-day window ending early
 * today). `now` is local wall-clock, matching the schedule's stored times.
 */
export function isWithinUnlockWindow(
  schedule: WeekSchedule,
  now: Date = new Date()
): boolean {
  const mins = now.getHours() * 60 + now.getMinutes();
  // JS getDay(): 0=Sun…6=Sat → UI day: 0=Mon…6=Sun.
  const todayUi = (now.getDay() + 6) % 7;
  const prevUi = (todayUi + 6) % 7;

  for (const w of schedule[todayUi] ?? []) {
    const s = w.startHour * 60 + w.startMinute;
    const e = w.endHour * 60 + w.endMinute;
    // Same-day window, or one that runs to/over midnight (e <= s).
    if (e > s ? mins >= s && mins < e : mins >= s) return true;
  }
  // A previous-day window that wraps past midnight covers the early hours today.
  for (const w of schedule[prevUi] ?? []) {
    const s = w.startHour * 60 + w.startMinute;
    const e = w.endHour * 60 + w.endMinute;
    if (e <= s && mins < e) return true;
  }
  return false;
}

function parseEntryList(raw: unknown): ScheduleEntryDto[] {
  if (!Array.isArray(raw)) return [];
  const out: ScheduleEntryDto[] = [];
  for (const e of raw) {
    if (e && typeof e === "object") {
      const m = e as Record<string, unknown>;
      out.push({
        day: Number(m.day),
        locked: Number(m.locked),
        hour: Number(m.hour),
        minute: Number(m.minute),
      });
    }
  }
  return out;
}

// Tolerant extraction of stove settings from a device reply event. Handles the
// canonical { is_scheduler_enabled, schedule } shape, a { scheduler: {...} }
// wrapper, JSON-string payloads, and common envelope keys.
export function tryParseStoveSettings(value: unknown): StoveSettings | null {
  if (typeof value === "string") {
    try {
      return tryParseStoveSettings(JSON.parse(value));
    } catch {
      return null;
    }
  }
  if (!value || typeof value !== "object") return null;
  const map = value as Record<string, unknown>;

  if ("is_scheduler_enabled" in map) {
    const rawActive = map.is_scheduler_enabled;
    const active =
      typeof rawActive === "boolean"
        ? rawActive
          ? 1
          : 0
        : Number(rawActive) || 0;
    return { schedulerActive: active, entries: parseEntryList(map.schedule) };
  }

  const scheduler = map.scheduler;
  if (scheduler && typeof scheduler === "object") {
    const s = scheduler as Record<string, unknown>;
    if ("enabled" in s || "is_scheduler_enabled" in s) {
      const rawActive = s.is_scheduler_enabled ?? s.enabled;
      const active =
        typeof rawActive === "boolean"
          ? rawActive
            ? 1
            : 0
          : Number(rawActive) || 0;
      return {
        schedulerActive: active,
        entries: parseEntryList(s.schedule ?? s.entries),
      };
    }
  }

  for (const key of ["payload", "state", "data", "device_state", "stove_settings"]) {
    if (map[key] != null) {
      const hit = tryParseStoveSettings(map[key]);
      if (hit) return hit;
    }
  }
  return null;
}

// Fetch the schedule the device currently holds. Returns null when the reply
// carries no recognisable schedule (caller can treat as "empty / unknown").
export async function getSchedule(
  name: string
): Promise<{ schedule: WeekSchedule; schedulerActive: boolean } | null> {
  const res = await axiosClient.get(`sensors/${name}/GetSchedule`, {
    params: { t: Date.now() },
  });
  const reqId = extractReqId(res.data);
  if (!reqId) throw new Error("GetSchedule response missing req_id");

  const events = await pollMqttEvents(name, reqId, {
    timeoutMs: 15000,
    maxAttempts: 5,
    readyWhen: (evs) => evs.some((e) => tryParseStoveSettings(e) != null),
  });

  for (const event of events) {
    const settings = tryParseStoveSettings(event);
    if (settings) {
      return {
        schedule: dtoToSchedule(settings.entries),
        schedulerActive: settings.schedulerActive === 1,
      };
    }
  }
  return null;
}

// Push the schedule to the device and wait for its acknowledgement. Throws if
// the device doesn't confirm (e.g. offline).
export async function sendSchedule(
  name: string,
  schedule: WeekSchedule,
  schedulerActive: boolean
): Promise<void> {
  const dto = scheduleToDto(schedule, schedulerActive);
  const res = await axiosClient.post(`sensors/${name}/UpdateScheduler`, dto);
  const reqId = extractReqId(res.data);
  if (!reqId) throw new Error("UpdateScheduler response missing req_id");
  await pollMqttEvents(name, reqId, { maxAttempts: 5 });
}
